import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ShippingEstimate } from '@pedix-workspace/utils';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { IconEditComponent, IconClockComponent } from '@pedix-workspace/angular-ui-icons';
import { InputRadioGroupComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { CurrencyPipe } from '@angular/common';

export type ShippingOptionItem = {
  estimate: ShippingEstimate;
  label: string;
  description: string;
  price: number;
  deliveryTime: string;
  deliveryMode: string;
};

@Component({
  selector: 'pxw-shipping-options-selector',
  templateUrl: './shipping-options-selector.component.html',
  styleUrls: ['./shipping-options-selector.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    InputRadioGroupComponent,
    FormsModule,
    ButtonComponent,
    IconEditComponent,
    IconClockComponent,
    CurrencyPipe,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class ShippingOptionsSelectorComponent {
  private readonly t = inject(TranslocoService);

  readonly availableOptions = input.required<ShippingEstimate[]>();
  readonly selectedOption = input<ShippingEstimate | null>(null);
  readonly isFormSubmitted = input.required<boolean>();

  readonly selectedOptionChange = output<ShippingEstimate>();

  readonly selectedOptionInternal = signal<ShippingEstimate | null>(null);

  readonly shippingOptions = computed<ShippingOptionItem[]>(() => {
    return this.availableOptions().map(estimate => {
      const additionalInfo = estimate.additionalInfo ? JSON.parse(estimate.additionalInfo) : {};
      
      return {
        estimate,
        label: this.buildServiceLabel(estimate, additionalInfo),
        description: this.buildDescription(estimate, additionalInfo),
        price: estimate.price,
        deliveryTime: this.formatDeliveryTime(estimate, additionalInfo),
        deliveryMode: additionalInfo.deliveryModeText || estimate.deliveryMode || '',
      };
    });
  });

  constructor() {
    effect(() => {
      this.selectedOptionInternal.set(this.selectedOption());
    }, { allowSignalWrites: true });

    effect(() => {
      const selected = this.selectedOptionInternal();
      if (selected) {
        this.selectedOptionChange.emit(selected);
      }
    });
  }

  private buildServiceLabel(estimate: ShippingEstimate, additionalInfo: any): string {
    // Construir el nombre del servicio limpio
    if (additionalInfo.productName) {
      // Remover "domicilio" del nombre y simplificar
      let serviceName = additionalInfo.productName
        .replace(/\s*-\s*Domicilio/i, '')
        .replace(/Correo Argentino/i, 'Correo Argentino');
      
      // Mapear nombres específicos
      if (serviceName.toLowerCase().includes('clasico')) {
        serviceName = 'Correo Argentino Clásico';
      } else if (serviceName.toLowerCase().includes('expreso')) {
        serviceName = 'Correo Argentino Expreso';
      }
      
      return serviceName;
    }
    
    return additionalInfo.serviceDescription || estimate.deliveryMode || 'Opción de envío';
  }

  private buildDescription(estimate: ShippingEstimate, additionalInfo: any): string {
    const parts: string[] = [];

    // Simplificar descripción, solo mantener el nombre del servicio principal
    if (additionalInfo.productName && !additionalInfo.productName.toLowerCase().includes('domicilio')) {
      // Remover "domicilio" del nombre y simplificar
      let serviceName = additionalInfo.productName
        .replace(/\s*-\s*Domicilio/i, '')
        .replace(/Correo Argentino/i, 'Correo Argentino');
      
      // Mapear nombres específicos
      if (serviceName.toLowerCase().includes('clasico')) {
        serviceName = 'Correo Argentino Clásico';
      } else if (serviceName.toLowerCase().includes('expreso')) {
        serviceName = 'Correo Argentino Expreso';
      }
      
      parts.push(serviceName);
    }

    return parts.join(' • ');
  }

  private formatDeliveryTime(estimate: ShippingEstimate, additionalInfo: any): string {
    // Si tenemos deliveryTime específico (fecha), usarlo
    if (estimate.deliveryTime) {
      return this.formatDeliveryDate(estimate.deliveryTime, additionalInfo);
    }

    // Fallback a los días hábiles
    const min = additionalInfo.deliveryTimeMin;
    const max = additionalInfo.deliveryTimeMax;

    if (min && max) {
      if (min === max) {
        if (min === 1) {
          return 'Entrega en 1 día hábil';
        }
        return `Entrega en ${max} días hábiles`;
      }
      return `Entrega en ${min}-${max} días hábiles`;
    }

    if (max) {
      const days = parseInt(max);
      if (days === 1) {
        return 'Entrega en 1 día hábil';
      }
      return `Entrega en ${days} días hábiles`;
    }

    return '';
  }

  formatDeliveryDate(dateString: string, additionalInfo?: any): string {
    try {
      const date = new Date(dateString);
      
      // Si tenemos información de rango de días, calcular rango de fechas
      if (additionalInfo?.deliveryTimeMin && additionalInfo?.deliveryTimeMax) {
        const min = parseInt(additionalInfo.deliveryTimeMin);
        const max = parseInt(additionalInfo.deliveryTimeMax);
        
        if (min !== max) {
          // Calcular fecha mínima y máxima
          const minDate = new Date();
          const maxDate = new Date();
          
          // Agregar días hábiles para fecha mínima
          let addedDays = 0;
          while (addedDays < min) {
            minDate.setDate(minDate.getDate() + 1);
            const dayOfWeek = minDate.getDay();
            if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Lunes a Viernes
              addedDays++;
            }
          }
          
          // Agregar días hábiles para fecha máxima
          addedDays = 0;
          while (addedDays < max) {
            maxDate.setDate(maxDate.getDate() + 1);
            const dayOfWeek = maxDate.getDay();
            if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Lunes a Viernes
              addedDays++;
            }
          }
          
          const minFormatted = minDate.toLocaleDateString('es-AR', {
            day: 'numeric',
            month: 'long'
          });
          const minWeekday = minDate.toLocaleDateString('es-AR', { weekday: 'long' });
          
          const maxFormatted = maxDate.toLocaleDateString('es-AR', {
            day: 'numeric',
            month: 'long'
          });
          const maxWeekday = maxDate.toLocaleDateString('es-AR', { weekday: 'long' });
          
          return `La entrega estimada es entre el ${minWeekday} ${minFormatted} y el ${maxWeekday} ${maxFormatted}`;
        }
      }
      
      // Fecha específica sin año
      const dayMonth = date.toLocaleDateString('es-AR', {
        day: 'numeric', 
        month: 'long'
      });
      const weekday = date.toLocaleDateString('es-AR', { weekday: 'long' });
      
      return `Entrega estimada: ${weekday} ${dayMonth}`;
    } catch {
      return 'Fecha de entrega por confirmar';
    }
  }

  private formatDeliveryTimeRange(additionalInfo: any): string {
    const min = additionalInfo.deliveryTimeMin;
    const max = additionalInfo.deliveryTimeMax;

    if (min && max) {
      if (min === max) {
        return `${max} día${max > 1 ? 's' : ''} hábil${max > 1 ? 'es' : ''}`;
      }
      return `${min}-${max} días hábiles`;
    }

    if (max) {
      return `hasta ${max} día${max > 1 ? 's' : ''} hábil${max > 1 ? 'es' : ''}`;
    }

    return '';
  }
} 